import React, { useEffect, useState, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import { ScentworkScoreDto, ScentworkFaultDto, IShow, IShowElement } from "@eagerdog/interfaces";

import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import StopWatch from "../StopWatch/StopWatch";

import styles from "./ScentworkResults.module.scss";

interface IProps {
  result: any,
  show?: IShow,
  onChange(result: ScentworkScoreDto): void
}

interface IScentworkShowElement extends IShowElement {
  num_hides?: number;
  time_limit?: string;
}

const ScentworkResults: React.FC<IProps> = (props) => {
  const nqReasons:IOption[] = [
    { id: Constants.scentwork_nq_reason.incorrect_alert_finish_call, value: Constants.scentwork_nq_reason.incorrect_alert_finish_call },
    { id: Constants.scentwork_nq_reason.harsh_correction, value: Constants.scentwork_nq_reason.harsh_correction },
    { id: Constants.scentwork_nq_reason.significant_disruption_of_area, value: Constants.scentwork_nq_reason.significant_disruption_of_area },
    { id: Constants.scentwork_nq_reason.handler_unable_to_point_to_hide, value: Constants.scentwork_nq_reason.handler_unable_to_point_to_hide },
    { id: Constants.scentwork_nq_reason.max_time_exceeded, value: Constants.scentwork_nq_reason.max_time_exceeded }
  ];

  const excusedReasons:IOption[] = [
    { id: Constants.scentwork_excused_reason.dog_eliminated_in_search_area, value: Constants.scentwork_excused_reason.dog_eliminated_in_search_area },
    { id: Constants.scentwork_excused_reason.dog_overly_stressed, value: Constants.scentwork_excused_reason.dog_overly_stressed },
    { id: Constants.scentwork_excused_reason.handler_request, value: Constants.scentwork_excused_reason.handler_request },
    { id: Constants.scentwork_excused_reason.other, value: Constants.scentwork_excused_reason.other }
  ];

  const getOptionLabel = (id: string) => {
    switch (id) {
      case Constants.score_result_type.qualified:
        return "Qualified";
      case Constants.score_result_type.not_qualified:
        return "Not Qualified";
      case Constants.score_result_type.disqualified:
        return "Disqualified";
      case Constants.score_result_type.excused:
        return "Excused";
      case Constants.score_result_type.absent:
        return "Absent";
    }

    return "None";
  }

  const getFault = (faultType: string) => {
    if (props.result.scentwork?.faults) {
      let hasFault = props.result.scentwork.faults.filter((f: ScentworkFaultDto) => { return f.fault_type === faultType; });

      if (hasFault.length > 0) {
        return hasFault[0].fault_count.toString();
      }
    }

    return "0";
  }

  // Get element configuration from show data
  const getElementConfig = () => {
    if (!props.show || !props.result.dog_class) {
      return { num_hides: 0, time_limit: "", hasConfig: false };
    }

    // First try to find exact match with both element and level
    let element = props.show.show_elements.find((el: IScentworkShowElement) => 
      el.show_element === props.result.dog_class.show_element &&
      el.level === props.result.dog_class.level
    ) as IScentworkShowElement;

    // If no exact match found, try to find by element only (for cases where level might not be set)
    if (!element) {
      element = props.show.show_elements.find((el: IScentworkShowElement) => 
        el.show_element === props.result.dog_class.show_element
      ) as IScentworkShowElement;
    }

    const hasConfig = element && ((element.num_hides && element.num_hides > 0) || element.time_limit);

    return {
      num_hides: element?.num_hides || 0,
      time_limit: element?.time_limit || "",
      hasConfig: !!hasConfig,
      elementFound: !!element
    };
  };

  const elementConfig = getElementConfig();

  const requiresNqReason = useMemo(() => {
    return [
      Constants.score_result_type.not_qualified,
      Constants.score_result_type.disqualified
    ]
  }, []);

  const requiresExcusedReason = useMemo(() => {
    return [
      Constants.score_result_type.excused
    ]
  }, []);

  const [result, setResult] = useState<IOption>({ id: props.result.scentwork?.result_type ? props.result.scentwork?.result_type : "None", value: props.result.scentwork?.result_type ? getOptionLabel(props.result.scentwork?.result_type) : "None" });
  const [searchTime, setSearchTime] = useState<string>(props.result.scentwork?.search_time ? props.result.scentwork?.search_time.toString() : "");
  const [numFinds, setNumFinds] = useState<string>(props.result.scentwork?.num_finds ? props.result.scentwork?.num_finds.toString() : "0");

  const [handlerError, setHandlerError] = useState<string>(getFault(Constants.scentwork_fault_type.handler_error));
  const [safetyConcern, setSafetyConcern] = useState<string>(getFault(Constants.scentwork_fault_type.safety_concern));
  const [mdoa, setMdoa] = useState<string>(getFault(Constants.scentwork_fault_type.mild_disruption_of_area));

  const [nqReason, setNqReason] = useState<IOption>(props.result.scentwork?.result_reason ? { id: props.result.scentwork.result_reason || "", value: props.result.scentwork.result_reason || "" } : { id: Constants.scentwork_nq_reason.incorrect_alert_finish_call, value: Constants.scentwork_nq_reason.incorrect_alert_finish_call });
  const [excusedReason, setExcusedReason] = useState<IOption>(props.result.scentwork?.result_reason ? { id: props.result.scentwork.result_reason || "", value: props.result.scentwork.result_reason || "" } : { id: Constants.scentwork_excused_reason.dog_eliminated_in_search_area, value: Constants.scentwork_excused_reason.dog_eliminated_in_search_area });
  const [otherReason, setOtherReason] = useState<string>(props.result.scentwork?.result_other_text ? props.result.scentwork?.result_other_text : "");

  const resultOptions:IOption[] = [
    { id: "None", value: "None" },
    { id: Constants.score_result_type.qualified, value: "Qualified" },
    { id: Constants.score_result_type.not_qualified, value: "Not Qualified" },
    { id: Constants.score_result_type.disqualified, value: "Disqualified" },
    { id: Constants.score_result_type.excused, value: "Excused" },
    { id: Constants.score_result_type.absent, value: "Absent" }
  ]

  const [hasChanged, setHasChanged] = useState<boolean>(false);

  useEffect(() => {
    if (hasChanged) {
      let _payload: ScentworkScoreDto = {
        time_limit: props.result.scentwork?.time_limit || "",
        num_hides: props.result.scentwork?.num_hides || 0,
        num_finds: Number(numFinds),
        faults: [
          { fault_type: Constants.scentwork_fault_type.handler_error, fault_count: Number(handlerError) },
          { fault_type: Constants.scentwork_fault_type.safety_concern, fault_count: Number(safetyConcern) },
          { fault_type: Constants.scentwork_fault_type.mild_disruption_of_area, fault_count: Number(mdoa) }
        ],
        result_type: result.id,
        search_time: searchTime,
      };

      if (requiresNqReason.includes(result.id)) {
        _payload.result_reason = nqReason.id;
      }

      if (requiresExcusedReason.includes(result.id) && excusedReason.id !== Constants.scentwork_excused_reason.other) {
        _payload.result_reason = excusedReason.id;
      }

      if (requiresExcusedReason.includes(result.id) && excusedReason.id === Constants.scentwork_excused_reason.other) {
        _payload.result_reason = excusedReason.id;
        _payload.result_other_text = otherReason;
      }

      props.onChange(_payload);

      setHasChanged(false);
    }
  }, [hasChanged, props, searchTime, result.id, excusedReason.id, handlerError, mdoa, nqReason.id, numFinds, otherReason, requiresExcusedReason, requiresNqReason, safetyConcern]);

  return (
    <div className={styles.ScentworkResults}>
      {elementConfig.hasConfig ? (
        <div className={styles.elementConfig}>
          <div className={styles.configTitle}>Element Configuration</div>
          <div className={styles.configDetails}>
            {elementConfig.num_hides > 0 && (
              <span><strong>Number of Hides:</strong> {elementConfig.num_hides}</span>
            )}
            {elementConfig.time_limit && (
              <span><strong>Time Limit:</strong> {elementConfig.time_limit}</span>
            )}
          </div>
        </div>
      ) : elementConfig.elementFound ? (
        <div className={styles.elementNote}>
          <p><strong>Note:</strong> Element configuration found but no values set. Configure in Element Settings tab.</p>
        </div>
      ) : (
        <div className={styles.elementNote}>
          <p><strong>Note:</strong> Time limits and number of hides are now configured in the Element Settings tab for this show.</p>
        </div>
      )}
      
      <div className={styles.row}><Dropdown value={result} onChange={(e: any, value: IOption) => { setResult(value); setHasChanged(true); }} label="Result" options={resultOptions} placeholder="" /></div>
      {requiresNqReason.includes(result.id) && 
        <div className="reason">
          <Dropdown value={nqReason} label={"NQ Reason"} onChange={(e: any, value: IOption) => { setNqReason(value); setHasChanged(true); }} options={nqReasons} placeholder="" />
        </div>
      }
      {requiresExcusedReason.includes(result.id) && 
        <>
        <div className="reason">
          <Dropdown value={excusedReason} label={"Excused Reason"} onChange={(e: any, value: IOption) => { setExcusedReason(value); setHasChanged(true); }} options={excusedReasons} placeholder="" />
        </div>
        {excusedReason.id === Constants.scentwork_excused_reason.other &&
          <div className={styles.row}><Input label="Other Reason" id="otherReason" type="text" required onChange={(e) => { e.stopPropagation(); setOtherReason(e.target.value); setHasChanged(true); }} placeholder="" defaultValue={otherReason} /></div>
        }
        </>
      }
      <div className={styles.row}><StopWatch label={"Run Time"} time={searchTime} onChange={(value: string) => { setSearchTime(value); setHasChanged(true); }} /></div>
      <div className={styles.row}>
        <div className={styles.findsInputGroup}>
          <Input 
            label="# of Finds" 
            id="numFinds" 
            type="number" 
            min="0" 
            max={elementConfig.num_hides > 0 ? elementConfig.num_hides.toString() : undefined}
            required 
            onChange={(e) => { 
              e.stopPropagation(); 
              setNumFinds(e.target.value); 
              setHasChanged(true); 
            }} 
            placeholder="" 
            defaultValue={numFinds} 
          />
          {elementConfig.num_hides > 0 && (
            <span className={styles.findsCounter}>/ {elementConfig.num_hides}</span>
          )}
        </div>
      </div>
      <div className={styles.subTitle}>Faults</div>
      <div className={styles.row}><Input label="Handler Error" id="handlerError" type="number" min="0" required onChange={(e) => { e.stopPropagation(); setHandlerError(e.target.value); setHasChanged(true); }} placeholder="" defaultValue={handlerError} /></div>
      <div className={styles.row}><Input label="Safety Concern" id="safetyConcern" type="number" min="0" required onChange={(e) => { e.stopPropagation(); setSafetyConcern(e.target.value); setHasChanged(true); }} placeholder="" defaultValue={safetyConcern} /></div>
      <div className={styles.row}><Input label="Mild Disruption of Area" id="mdoa" type="number" min="0" required onChange={(e) => { e.stopPropagation(); setMdoa(e.target.value); setHasChanged(true); }} placeholder="" defaultValue={mdoa} /></div>
    </div>
  );
};

export default ScentworkResults;